import React, { useCallback } from 'react';
import { noop, find, isEmpty, castArray } from 'lodash';
import classnames from 'classnames';

// local dependencies
import { getLabel } from 'utils/getLabel';
import { ChoiceList } from 'modules/choice-list';
import { popover } from 'modules/popover';
import { Icon } from 'modules/icon-bank';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { select as Select } from 'ui/views/generic/select';
 *
 * // render
 * <Select propName='propValue'/>
 */
export const select = ( props ) => {

    // get selected option
    const selectedOption = find( props.options, { value: props.value } );

    // open popover options
    const openPopover = useCallback( ( element ) => {
        popover.open( {
            referenceElement: element,
            view: ChoiceList,
            tooltip: false,
            compact: true,
            props: {
                allowFilter: props.allowFilter,
                displaySelectAllOption: props.displaySelectAllOption,
                type: props.type,
                options: props.options,
                placeholder: props.placeholder,
                selected: isEmpty( props.value ) ? [] : castArray( props.value ),
                onChange: props.onChange,
                choiceListClassName: 'ui-select__choice-list',
            }
        } );
    }, [ props.value, props.options ] );

    // dynamic class name
    const widgetClassName = classnames( 'ui-select', {
        'ui-select--disabled': props.disabled,
    } );

    return (
        <div className={ widgetClassName }>
            <div className='ui-select__trigger' onClick={ ( event ) => openPopover( event.currentTarget ) }>
                {/* placeholder */}
                {
                    isEmpty( selectedOption ) ? (
                        <div className='ui-select__trigger__placeholder'>
                            { getLabel( props.placeholder ) }
                        </div>
                    ) : null
                }

                {/* label */}
                {
                    isEmpty( selectedOption ) ? null : (
                        <div className='ui-select__trigger__label'>
                            { getLabel( selectedOption.title ) }
                        </div>
                    )
                }

                {/* arrow */}
                <div className='ui-select__trigger__arrow'>
                    <Icon name='downArrow' width='14' height='14' />
                </div>
            </div>
        </div>
    );
};

// set display name
select.displayName = 'select';

// set default props
select.defaultProps = {
    options: [],
    placeholder: '',
    value: '',
    onChange: noop,
    flyoutWidth: 300,
    type: 'singleSelect',
    disabled: false,
    displaySelectAllOption: false,
    allowFilter: true,
};

// prop types
select.propTypes = {};
